import type { StyleSectionDescriptor } from '@examdojo/core/widget';
import type {
  AllElementType,
  AnnotationElementType,
  FeatureElementType,
  GeometryElementDef,
  GeometryElementType,
  GeometryDisplayConfig,
  GeometryElementDisplayConfig,
} from '../geometrypad.model';
import { GEOMETRY_DISPLAY_DEFAULTS } from '../geometrypad.model';

export type { AllElementType };
import { LENGTH_ANNOTATION_DEFAULTS } from '../elements/annotations/length-annotation/length-annotation.model';
import { ARROW_ANNOTATION_DEFAULTS } from '../elements/annotations/arrow-annotation/arrow-annotation.model';
import {
  TRIANGLE_VERTEX_NAMES,
  TRIANGLE_EDGE_NAMES,
  TRIANGLE_ANGLE_NAMES,
  TRIANGLE_PROPERTY_FOR_EDGE,
  TRIANGLE_DEFAULTS,
} from '../elements/shapes/triangle/triangle.model';
import {
  EDGE_NAMES as RECTANGLE_EDGE_NAMES,
  PROPERTY_FOR_EDGE as RECTANGLE_PROPERTY_FOR_EDGE,
  RECTANGLE_DEFAULTS,
} from '../elements/shapes/rectangle/rectangle.model';
import { SQUARE_EDGE_NAMES, SQUARE_PROPERTY_FOR_EDGE, SQUARE_DEFAULTS } from '../elements/shapes/square/square.model';
import {
  PARALLELOGRAM_EDGE_NAMES,
  PARALLELOGRAM_PROPERTY_FOR_EDGE,
  PARALLELOGRAM_DEFAULTS,
} from '../elements/shapes/parallelogram/parallelogram.model';
import {
  RHOMBUS_EDGE_NAMES,
  RHOMBUS_PROPERTY_FOR_EDGE,
  RHOMBUS_DEFAULTS,
} from '../elements/shapes/rhombus/rhombus.model';
import {
  TRAPEZOID_EDGE_NAMES,
  TRAPEZOID_PROPERTY_FOR_EDGE,
  TRAPEZOID_DEFAULTS,
} from '../elements/shapes/trapezoid/trapezoid.model';
import { CIRCLE_DEFAULTS } from '../elements/shapes/circle/circle.model';
import { ARC_DEFAULTS } from '../elements/shapes/arc/arc.model';

// =============================================================================
// RE-EXPORT DEFAULTS
// =============================================================================

export { GEOMETRY_DISPLAY_DEFAULTS };

// =============================================================================
// PARAM FIELD DEFINITIONS
// =============================================================================

export type ParamFieldType =
  | 'number'
  | 'value-expr'
  | 'coord'
  | 'coord-or-ref'
  | 'port-ref'
  | 'select'
  | 'string'
  | 'tuple-number';

export interface ParamFieldDef {
  readonly key: string;
  readonly label: string;
  readonly type: ParamFieldType;
  readonly options?: readonly string[];
  readonly optional?: boolean;
}

// =============================================================================
// ELEMENT CATEGORIES
// =============================================================================

export type GeometryEditorCategory = 'shape' | 'annotation' | 'feature';

const SHAPE_TYPES: readonly GeometryElementType[] = [
  'Triangle',
  'Rectangle',
  'Square',
  'Circle',
  'Arc',
  'Parallelogram',
  'Rhombus',
  'Trapezoid',
  'Point',
  'Line',
  'Polygon',
] as const;

const ANNOTATION_TYPES: readonly AnnotationElementType[] = ['LengthAnnotation', 'ArrowAnnotation'] as const;

const FEATURE_TYPES: readonly FeatureElementType[] = ['Diagonal', 'Altitude', 'Median', 'Midsegment'] as const;

export function getShapeTypes(): readonly GeometryElementType[] {
  return SHAPE_TYPES;
}

export function getAnnotationTypes(): readonly AnnotationElementType[] {
  return ANNOTATION_TYPES;
}

export function getFeatureTypes(): readonly FeatureElementType[] {
  return FEATURE_TYPES;
}

export function getAllElementTypes(): readonly AllElementType[] {
  return [...SHAPE_TYPES, ...ANNOTATION_TYPES, ...FEATURE_TYPES];
}

export function getElementCategory(type: AllElementType): GeometryEditorCategory {
  if ((SHAPE_TYPES as readonly string[]).includes(type)) {
    return 'shape';
  }
  if ((ANNOTATION_TYPES as readonly string[]).includes(type)) {
    return 'annotation';
  }
  return 'feature';
}

// =============================================================================
// DISPLAY STYLE KEYS
// =============================================================================

export type GeoDisplayStyleKey = 'point' | 'line' | 'polygon' | 'angle' | 'circle' | 'visible' | 'label';

export interface GeoDisplayStyleMeta {
  readonly keys: readonly GeoDisplayStyleKey[];
}

const GEO_DISPLAY_STYLE_MAP: Record<string, GeoDisplayStyleMeta> = {
  Triangle: { keys: ['point', 'line', 'polygon', 'angle', 'visible', 'label'] },
  Rectangle: { keys: ['point', 'line', 'polygon', 'angle', 'visible', 'label'] },
  Square: { keys: ['point', 'line', 'polygon', 'angle', 'visible', 'label'] },
  Circle: { keys: ['circle', 'point', 'visible', 'label'] },
  Arc: { keys: ['circle', 'line', 'point', 'angle', 'visible', 'label'] },
  Parallelogram: { keys: ['point', 'line', 'polygon', 'angle', 'visible', 'label'] },
  Rhombus: { keys: ['point', 'line', 'polygon', 'angle', 'visible', 'label'] },
  Trapezoid: { keys: ['point', 'line', 'polygon', 'angle', 'visible', 'label'] },
  Point: { keys: ['point', 'visible', 'label'] },
  Line: { keys: ['line', 'visible', 'label'] },
  Polygon: { keys: ['polygon', 'visible', 'label'] },
  LengthAnnotation: { keys: ['line', 'label', 'visible'] },
  ArrowAnnotation: { keys: ['line', 'label', 'visible'] },
  Diagonal: { keys: ['line', 'visible'] },
  Altitude: { keys: ['line', 'point', 'visible'] },
  Median: { keys: ['line', 'point', 'visible'] },
  Midsegment: { keys: ['line', 'point', 'visible'] },
};

export function getDisplayStyleMeta(type: string): GeoDisplayStyleMeta {
  return GEO_DISPLAY_STYLE_MAP[type] ?? { keys: ['visible'] };
}

// =============================================================================
// GENERIC STYLE EDITOR SECTION DESCRIPTORS
// =============================================================================

const GEO_FEATURE_LINE_DEFAULTS = { color: 'neutral', strokeWidth: 1, dash: 2 };

const QUAD_VERTEX_NAMES = ['A', 'B', 'C', 'D'] as const;

// -- Shape structure registry: defines vertices, edges, angles and defaults per shape --

interface ShapeStructure {
  readonly vertexNames: readonly string[];
  readonly edgeNames: readonly string[];
  readonly angleNames: readonly string[];
  readonly defaults: GeometryElementDisplayConfig;
}

const QUAD_ANGLE_NAMES = ['alpha', 'beta', 'gamma', 'delta'] as const;

const SHAPE_STRUCTURE_REGISTRY: Record<string, ShapeStructure> = {
  Triangle: {
    vertexNames: TRIANGLE_VERTEX_NAMES,
    edgeNames: TRIANGLE_EDGE_NAMES,
    angleNames: TRIANGLE_ANGLE_NAMES,
    defaults: TRIANGLE_DEFAULTS,
  },
  Rectangle: {
    vertexNames: QUAD_VERTEX_NAMES,
    edgeNames: RECTANGLE_EDGE_NAMES,
    angleNames: QUAD_ANGLE_NAMES,
    defaults: RECTANGLE_DEFAULTS,
  },
  Square: {
    vertexNames: QUAD_VERTEX_NAMES,
    edgeNames: SQUARE_EDGE_NAMES,
    angleNames: QUAD_ANGLE_NAMES,
    defaults: SQUARE_DEFAULTS,
  },
  Parallelogram: {
    vertexNames: QUAD_VERTEX_NAMES,
    edgeNames: PARALLELOGRAM_EDGE_NAMES,
    angleNames: QUAD_ANGLE_NAMES,
    defaults: PARALLELOGRAM_DEFAULTS,
  },
  Rhombus: {
    vertexNames: QUAD_VERTEX_NAMES,
    edgeNames: RHOMBUS_EDGE_NAMES,
    angleNames: QUAD_ANGLE_NAMES,
    defaults: RHOMBUS_DEFAULTS,
  },
  Trapezoid: {
    vertexNames: QUAD_VERTEX_NAMES,
    edgeNames: TRAPEZOID_EDGE_NAMES,
    angleNames: QUAD_ANGLE_NAMES,
    defaults: TRAPEZOID_DEFAULTS,
  },
};

/**
 * Build style sections for a composite shape from its structure registry entry.
 *
 * Section order: Vertices (global) → per-vertex → Edges (global) → per-edge → Fill → Angles (global) → per-angle
 * Global sections are style-only headers (no toggle); per-subpart sections have working toggles.
 */
function buildShapeStyleSections(structure: ShapeStructure): readonly StyleSectionDescriptor[] {
  const { vertexNames, edgeNames, angleNames, defaults } = structure;
  const subparts = defaults.subparts ?? {};
  const sections: StyleSectionDescriptor[] = [];

  // --- Vertices ---
  sections.push({
    name: 'Vertices',
    styleKey: 'point',
    showLabel: true,
    showVisible: false,
    defaults: (defaults.point as Record<string, unknown>) ?? {},
  });
  for (const v of vertexNames) {
    sections.push({
      name: `Point ${v}`,
      styleKey: 'point',
      subpartPath: [v],
      showLabel: true,
      showVisible: true,
      defaults: (subparts[v]?.point as Record<string, unknown>) ?? {},
    });
  }

  // --- Edges ---
  sections.push({
    name: 'Edges',
    styleKey: 'line',
    showLabel: true,
    showVisible: false,
    defaults: (defaults.line as Record<string, unknown>) ?? {},
  });
  for (const e of edgeNames) {
    sections.push({
      name: `Edge ${e}`,
      styleKey: 'line',
      subpartPath: [e],
      showLabel: true,
      showVisible: true,
      defaults: (subparts[e]?.line as Record<string, unknown>) ?? {},
    });
  }

  // --- Fill ---
  sections.push({
    name: 'Fill',
    styleKey: 'polygon',
    showVisible: true,
    defaults: (defaults.polygon as Record<string, unknown>) ?? {},
  });

  // --- Angles ---
  if (angleNames.length > 0) {
    sections.push({
      name: 'Angles',
      styleKey: 'angle',
      showLabel: true,
      showVisible: false,
      defaults: (defaults.angle as Record<string, unknown>) ?? {},
    });
    for (const a of angleNames) {
      sections.push({
        name: `Angle ${a}`,
        styleKey: 'angle',
        subpartPath: [a],
        showLabel: true,
        showVisible: true,
        defaults: (subparts[a]?.angle as Record<string, unknown>) ?? {},
      });
    }
  }

  return sections;
}

// -- Build the sections map from registry + manual entries for non-composite shapes --

const GEO_STYLE_SECTIONS_MAP: Record<string, readonly StyleSectionDescriptor[]> = {
  // Composite shapes — generated from structure registry
  ...Object.fromEntries(
    Object.entries(SHAPE_STRUCTURE_REGISTRY).map(([type, structure]) => [type, buildShapeStyleSections(structure)]),
  ),

  // Non-composite shapes — manual definitions
  Arc: [
    {
      name: 'Arc',
      styleKey: 'circle',
      showLabel: true,
      showVisible: true,
      defaults: (ARC_DEFAULTS.circle as Record<string, unknown>) ?? {},
    },
    {
      name: 'Center',
      styleKey: 'point',
      subpartPath: ['center'],
      showLabel: true,
      showVisible: true,
      defaults: (ARC_DEFAULTS.subparts?.['center']?.point as Record<string, unknown>) ?? {},
    },
    {
      name: 'Start Point',
      styleKey: 'point',
      subpartPath: ['start'],
      showLabel: true,
      showVisible: true,
      defaults: (ARC_DEFAULTS.subparts?.['start']?.point as Record<string, unknown>) ?? {},
    },
    {
      name: 'End Point',
      styleKey: 'point',
      subpartPath: ['end'],
      showLabel: true,
      showVisible: true,
      defaults: (ARC_DEFAULTS.subparts?.['end']?.point as Record<string, unknown>) ?? {},
    },
    {
      name: 'Chord',
      styleKey: 'line',
      subpartPath: ['chord'],
      showLabel: true,
      showVisible: true,
      defaults: (ARC_DEFAULTS.subparts?.['chord']?.line as Record<string, unknown>) ?? {},
    },
    {
      name: 'Radius (Start)',
      styleKey: 'line',
      subpartPath: ['radiusStart'],
      showLabel: true,
      showVisible: true,
      defaults: (ARC_DEFAULTS.subparts?.['radiusStart']?.line as Record<string, unknown>) ?? {},
    },
    {
      name: 'Radius (End)',
      styleKey: 'line',
      subpartPath: ['radiusEnd'],
      showLabel: true,
      showVisible: true,
      defaults: (ARC_DEFAULTS.subparts?.['radiusEnd']?.line as Record<string, unknown>) ?? {},
    },
    {
      name: 'Central Angle',
      styleKey: 'angle',
      subpartPath: ['centralAngle'],
      showLabel: true,
      showVisible: true,
      defaults: (ARC_DEFAULTS.subparts?.['centralAngle']?.angle as Record<string, unknown>) ?? {},
    },
    {
      name: 'Sector Fill',
      styleKey: 'polygon',
      subpartPath: ['sector'],
      showVisible: true,
      defaults: (ARC_DEFAULTS.subparts?.['sector']?.polygon as Record<string, unknown>) ?? {},
    },
  ],
  Circle: [
    {
      name: 'Circle',
      styleKey: 'circle',
      showLabel: true,
      showVisible: true,
      defaults: (CIRCLE_DEFAULTS.circle as Record<string, unknown>) ?? {},
    },
    {
      name: 'Center',
      styleKey: 'point',
      subpartPath: ['center'],
      showLabel: true,
      showVisible: true,
      defaults: (CIRCLE_DEFAULTS.subparts?.['center']?.point as Record<string, unknown>) ?? {},
    },
  ],
  Point: [
    { name: 'Point', styleKey: 'point', showLabel: true, showVisible: true, defaults: { color: 'sky', size: 4 } },
  ],
  Line: [
    {
      name: 'Line',
      styleKey: 'line',
      showLabel: true,
      showVisible: true,
      defaults: { color: 'neutral', strokeWidth: 2 },
    },
  ],
  Polygon: [
    { name: 'Fill', styleKey: 'polygon', showVisible: true, defaults: { fillColor: 'neutral', fillOpacity: 0.1 } },
    {
      name: 'Border',
      styleKey: 'line',
      showLabel: true,
      showVisible: true,
      defaults: { color: 'neutral', strokeWidth: 2 },
    },
  ],

  // Annotations
  LengthAnnotation: [
    { name: 'Line', styleKey: 'line', showLabel: true, showVisible: true, defaults: GEO_FEATURE_LINE_DEFAULTS },
  ],
  ArrowAnnotation: [
    {
      name: 'Arrow',
      styleKey: 'line',
      showLabel: true,
      showVisible: true,
      defaults: { color: 'neutral', strokeWidth: 1.5 },
    },
  ],

  // Features
  Diagonal: [
    { name: 'Line', styleKey: 'line', showLabel: true, showVisible: true, defaults: GEO_FEATURE_LINE_DEFAULTS },
  ],
  Altitude: [
    { name: 'Line', styleKey: 'line', showLabel: true, showVisible: true, defaults: GEO_FEATURE_LINE_DEFAULTS },
    { name: 'Foot Point', styleKey: 'point', showLabel: true, showVisible: true, defaults: { color: 'sky', size: 4 } },
  ],
  Median: [
    { name: 'Line', styleKey: 'line', showLabel: true, showVisible: true, defaults: GEO_FEATURE_LINE_DEFAULTS },
    { name: 'Midpoint', styleKey: 'point', showLabel: true, showVisible: true, defaults: { color: 'sky', size: 4 } },
  ],
  Midsegment: [
    { name: 'Line', styleKey: 'line', showLabel: true, showVisible: true, defaults: GEO_FEATURE_LINE_DEFAULTS },
    { name: 'Midpoints', styleKey: 'point', showLabel: true, showVisible: true, defaults: { color: 'sky', size: 4 } },
  ],
};

export function getGeoStyleSections(type: string): readonly StyleSectionDescriptor[] {
  return GEO_STYLE_SECTIONS_MAP[type] ?? [];
}

// =============================================================================
// ELEMENT METADATA
// =============================================================================

export interface EditorElementMeta {
  readonly label: string;
  readonly category: GeometryEditorCategory;
  /** Parameter fields. Use '_default' key. */
  readonly paramsByFormat: Record<string, readonly ParamFieldDef[]>;
  /** Whether this element supports custom names for properties */
  readonly hasNames?: boolean;
  readonly nameFields?: readonly string[];
  /** Whether this element requires a parent element (features) */
  readonly requiresParent?: boolean;
  /** Valid parent element types for features */
  readonly validParentTypes?: readonly string[];
  /** Default parameters for creating a new element of this type */
  readonly defaultParams: Record<string, unknown>;
  /** Display style keys applicable to this element */
  readonly displayKeys: readonly GeoDisplayStyleKey[];
}

// --- Scalar param fields per shape (format auto-detected by solver) ---
const TRIANGLE_PARAMS: readonly ParamFieldDef[] = [
  { key: 'a', label: 'Side a', type: 'value-expr', optional: true },
  { key: 'b', label: 'Side b', type: 'value-expr', optional: true },
  { key: 'c', label: 'Side c', type: 'value-expr', optional: true },
  { key: 'alpha', label: 'Angle α (°)', type: 'value-expr', optional: true },
  { key: 'beta', label: 'Angle β (°)', type: 'value-expr', optional: true },
  { key: 'gamma', label: 'Angle γ (°)', type: 'value-expr', optional: true },
];

const RECTANGLE_PARAMS: readonly ParamFieldDef[] = [
  { key: 'w', label: 'Width', type: 'value-expr', optional: true },
  { key: 'h', label: 'Height', type: 'value-expr', optional: true },
  { key: 'd', label: 'Diagonal', type: 'value-expr', optional: true },
];

const CIRCLE_PARAMS: readonly ParamFieldDef[] = [{ key: 'r', label: 'Radius', type: 'value-expr', optional: true }];

const ELEMENT_META_MAP: Record<string, EditorElementMeta> = {
  // ═══ SHAPES ═══
  Triangle: {
    label: 'Triangle',
    category: 'shape',
    paramsByFormat: {
      _default: [...TRIANGLE_PARAMS],
    },
    hasNames: true,
    nameFields: ['a', 'b', 'c', 'alpha', 'beta', 'gamma'],
    defaultParams: { a: 5, b: 4, c: 3 },
    displayKeys: ['point', 'line', 'polygon', 'angle', 'visible', 'label'],
  },

  Rectangle: {
    label: 'Rectangle',
    category: 'shape',
    paramsByFormat: {
      _default: [...RECTANGLE_PARAMS],
    },
    hasNames: true,
    nameFields: ['w', 'h', 'd'],
    defaultParams: { w: 5, h: 3 },
    displayKeys: ['point', 'line', 'polygon', 'angle', 'visible', 'label'],
  },

  Square: {
    label: 'Square',
    category: 'shape',
    paramsByFormat: {
      _default: [{ key: 's', label: 'Side', type: 'value-expr', optional: true }],
    },
    hasNames: true,
    nameFields: ['s', 'd'],
    defaultParams: { s: 4 },
    displayKeys: ['point', 'line', 'polygon', 'angle', 'visible', 'label'],
  },

  Circle: {
    label: 'Circle',
    category: 'shape',
    paramsByFormat: {
      _default: [...CIRCLE_PARAMS],
    },
    hasNames: true,
    nameFields: ['r', 'd'],
    defaultParams: { r: 3 },
    displayKeys: ['circle', 'point', 'visible', 'label'],
  },

  Arc: {
    label: 'Arc',
    category: 'shape',
    paramsByFormat: {
      _default: [
        { key: 'r', label: 'Radius', type: 'value-expr', optional: true },
        { key: 'startAngle', label: 'Start Angle (°)', type: 'value-expr', optional: true },
        { key: 'endAngle', label: 'End Angle (°)', type: 'value-expr', optional: true },
        { key: 'centralAngle', label: 'Central Angle (°)', type: 'value-expr', optional: true },
      ],
    },
    hasNames: true,
    nameFields: ['r', 'arcLength', 'centralAngle', 'chordLength', 'sectorArea', 'segmentArea'],
    defaultParams: { r: 3, startAngle: 0, centralAngle: 90 },
    displayKeys: ['circle', 'line', 'point', 'angle', 'visible', 'label'],
  },

  Parallelogram: {
    label: 'Parallelogram',
    category: 'shape',
    paramsByFormat: {
      _default: [
        { key: 'b', label: 'Base', type: 'value-expr' },
        { key: 's', label: 'Side', type: 'value-expr' },
        { key: 'angle', label: 'Angle (°)', type: 'value-expr' },
      ],
    },
    hasNames: true,
    nameFields: ['b', 's', 'angle', 'h', 'd1', 'd2'],
    defaultParams: { b: 5, s: 3, angle: 60 },
    displayKeys: ['point', 'line', 'polygon', 'angle', 'visible', 'label'],
  },

  Rhombus: {
    label: 'Rhombus',
    category: 'shape',
    paramsByFormat: {
      _default: [
        { key: 's', label: 'Side', type: 'value-expr' },
        { key: 'angle', label: 'Angle (°)', type: 'value-expr', optional: true },
      ],
    },
    hasNames: true,
    nameFields: ['s', 'angle', 'h', 'd1', 'd2'],
    defaultParams: { s: 4, angle: 60 },
    displayKeys: ['point', 'line', 'polygon', 'angle', 'visible', 'label'],
  },

  Trapezoid: {
    label: 'Trapezoid',
    category: 'shape',
    paramsByFormat: {
      _default: [
        { key: 'a', label: 'Base a (bottom)', type: 'value-expr' },
        { key: 'b', label: 'Base b (top)', type: 'value-expr' },
        { key: 'h', label: 'Height', type: 'value-expr' },
        { key: 'angle', label: 'Angle (°)', type: 'value-expr', optional: true },
      ],
    },
    hasNames: true,
    nameFields: ['a', 'b', 'h', 'angle', 's1', 's2', 'd1', 'd2'],
    defaultParams: { a: 6, b: 3, h: 3 },
    displayKeys: ['point', 'line', 'polygon', 'angle', 'visible', 'label'],
  },

  Point: {
    label: 'Point',
    category: 'shape',
    paramsByFormat: {
      _default: [
        { key: 'x', label: 'X', type: 'value-expr' },
        { key: 'y', label: 'Y', type: 'value-expr' },
        { key: 'glideOn', label: 'Glide on', type: 'port-ref', optional: true },
      ],
    },
    defaultParams: { x: 0, y: 0 },
    displayKeys: ['point', 'visible', 'label'],
  },

  Line: {
    label: 'Line',
    category: 'shape',
    paramsByFormat: {
      _default: [
        { key: 'from', label: 'From', type: 'coord-or-ref' },
        { key: 'to', label: 'To', type: 'coord-or-ref' },
      ],
    },
    defaultParams: { from: { x: -1, y: 0 }, to: { x: 1, y: 0 } },
    displayKeys: ['line', 'visible', 'label'],
  },

  Polygon: {
    label: 'Polygon',
    category: 'shape',
    paramsByFormat: {
      _default: [],
    },
    defaultParams: {
      vertices: [
        { x: 0, y: 0 },
        { x: 2, y: 0 },
        { x: 1, y: 2 },
      ],
    },
    displayKeys: ['polygon', 'visible', 'label'],
  },

  // ═══ ANNOTATIONS ═══
  LengthAnnotation: {
    label: 'Length Annotation',
    category: 'annotation',
    paramsByFormat: {
      _default: [
        { key: 'of', label: 'Edge ref', type: 'port-ref' },
        { key: 'offset', label: 'Offset', type: 'number', optional: true },
      ],
    },
    defaultParams: { of: '', offset: LENGTH_ANNOTATION_DEFAULTS.offset },
    displayKeys: ['line', 'label', 'visible'],
  },

  ArrowAnnotation: {
    label: 'Arrow Annotation',
    category: 'annotation',
    paramsByFormat: {
      _default: [
        { key: 'target', label: 'Target ref', type: 'port-ref' },
        { key: 'label', label: 'Label text', type: 'string' },
        { key: 'offset', label: 'Offset [dx, dy]', type: 'tuple-number', optional: true },
        {
          key: 'routing',
          label: 'Routing',
          type: 'select',
          options: ['straight', 'orthogonal', 'horizontal-first', 'vertical-first'],
          optional: true,
        },
      ],
    },
    defaultParams: {
      target: '',
      label: 'Label',
      offset: [...ARROW_ANNOTATION_DEFAULTS.offset],
      routing: ARROW_ANNOTATION_DEFAULTS.routing,
    },
    displayKeys: ['line', 'label', 'visible'],
  },

  // ═══ FEATURES ═══
  Diagonal: {
    label: 'Diagonal',
    category: 'feature',
    requiresParent: true,
    validParentTypes: ['Rectangle', 'Square', 'Parallelogram', 'Rhombus', 'Trapezoid'],
    paramsByFormat: {
      _default: [{ key: 'vertices', label: 'Vertices (optional)', type: 'string', optional: true }],
    },
    defaultParams: { of: '' },
    displayKeys: ['line', 'visible'],
  },

  Altitude: {
    label: 'Altitude',
    category: 'feature',
    requiresParent: true,
    validParentTypes: ['Triangle', 'Parallelogram', 'Trapezoid'],
    paramsByFormat: {
      _default: [{ key: 'from', label: 'From vertex', type: 'string' }],
    },
    defaultParams: { of: '', from: 'A' },
    displayKeys: ['line', 'point', 'visible'],
  },

  Median: {
    label: 'Median',
    category: 'feature',
    requiresParent: true,
    validParentTypes: ['Triangle'],
    paramsByFormat: {
      _default: [{ key: 'from', label: 'From vertex', type: 'string' }],
    },
    defaultParams: { of: '', from: 'A' },
    displayKeys: ['line', 'point', 'visible'],
  },

  Midsegment: {
    label: 'Midsegment',
    category: 'feature',
    requiresParent: true,
    validParentTypes: ['Triangle', 'Trapezoid'],
    paramsByFormat: {
      _default: [{ key: 'edges', label: 'Edges [e1, e2]', type: 'string' }],
    },
    defaultParams: { of: '', edges: ['AB', 'BC'] },
    displayKeys: ['line', 'point', 'visible'],
  },
};

export function getElementMeta(type: string): EditorElementMeta | undefined {
  return ELEMENT_META_MAP[type];
}

export function getDefaultElement(type: AllElementType): GeometryElementDef {
  const meta = ELEMENT_META_MAP[type];
  if (!meta) {
    return { type: 'Point', params: { x: 0, y: 0 } } as GeometryElementDef;
  }
  return { type, params: { ...meta.defaultParams } } as unknown as GeometryElementDef;
}

// =============================================================================
// TYPE GUARDS
// =============================================================================

export function isShapeElement(def: GeometryElementDef): boolean {
  return (SHAPE_TYPES as readonly string[]).includes(def.type);
}

export function isAnnotationElement(def: GeometryElementDef): boolean {
  return (ANNOTATION_TYPES as readonly string[]).includes(def.type);
}

export function isFeatureElement(def: GeometryElementDef): boolean {
  return (FEATURE_TYPES as readonly string[]).includes(def.type);
}

// =============================================================================
// PORT GROUPS REGISTRY (single source of truth for all element port groups)
// =============================================================================

export interface GeometryPortGroup {
  readonly label: string;
  readonly ports: readonly string[];
}

const PORT_GROUPS_BY_TYPE: Record<string, (params?: Record<string, unknown>) => GeometryPortGroup[]> = {
  Triangle: () => [
    { label: 'Points', ports: ['point.A', 'point.B', 'point.C', 'angleCenter.A', 'angleCenter.B', 'angleCenter.C'] },
    { label: 'Edges', ports: ['edge.AB', 'edge.BC', 'edge.CA'] },
    { label: 'Angles', ports: ['alpha', 'beta', 'gamma', 'extAlpha', 'extBeta', 'extGamma'] },
    { label: 'Scalars', ports: ['a', 'b', 'c', 'area', 'perimeter'] },
  ],
  Rectangle: () => [
    {
      label: 'Points',
      ports: [
        'point.A',
        'point.B',
        'point.C',
        'point.D',
        'angleCenter.A',
        'angleCenter.B',
        'angleCenter.C',
        'angleCenter.D',
      ],
    },
    { label: 'Edges', ports: ['edge.bottom', 'edge.right', 'edge.top', 'edge.left'] },
    { label: 'Scalars', ports: ['w', 'h', 'd', 'area', 'perimeter'] },
  ],
  Square: () => [
    {
      label: 'Points',
      ports: [
        'point.A',
        'point.B',
        'point.C',
        'point.D',
        'angleCenter.A',
        'angleCenter.B',
        'angleCenter.C',
        'angleCenter.D',
      ],
    },
    { label: 'Edges', ports: ['edge.bottom', 'edge.right', 'edge.top', 'edge.left'] },
    { label: 'Scalars', ports: ['s', 'w', 'h', 'd', 'area', 'perimeter'] },
  ],
  Circle: () => [
    { label: 'Points', ports: ['center'] },
    { label: 'Scalars', ports: ['radius', 'area', 'circumference'] },
  ],
  Arc: () => [
    { label: 'Points', ports: ['center', 'startPoint', 'endPoint', 'midPoint'] },
    {
      label: 'Scalars',
      ports: [
        'radius',
        'startAngle',
        'endAngle',
        'centralAngle',
        'arcLength',
        'chordLength',
        'sectorArea',
        'segmentArea',
      ],
    },
    { label: 'Elements', ports: ['arc'] },
  ],
  Parallelogram: () => [
    {
      label: 'Points',
      ports: [
        'point.A',
        'point.B',
        'point.C',
        'point.D',
        'angleCenter.A',
        'angleCenter.B',
        'angleCenter.C',
        'angleCenter.D',
      ],
    },
    { label: 'Edges', ports: ['edge.bottom', 'edge.right', 'edge.top', 'edge.left'] },
    { label: 'Angles', ports: ['angle'] },
    { label: 'Scalars', ports: ['b', 's', 'h', 'd1', 'd2', 'area', 'perimeter'] },
  ],
  Rhombus: () => [
    {
      label: 'Points',
      ports: [
        'point.A',
        'point.B',
        'point.C',
        'point.D',
        'angleCenter.A',
        'angleCenter.B',
        'angleCenter.C',
        'angleCenter.D',
      ],
    },
    { label: 'Edges', ports: ['edge.bottom', 'edge.right', 'edge.top', 'edge.left'] },
    { label: 'Angles', ports: ['angle'] },
    { label: 'Scalars', ports: ['s', 'h', 'd1', 'd2', 'area', 'perimeter'] },
  ],
  Trapezoid: () => [
    {
      label: 'Points',
      ports: [
        'point.A',
        'point.B',
        'point.C',
        'point.D',
        'angleCenter.A',
        'angleCenter.B',
        'angleCenter.C',
        'angleCenter.D',
      ],
    },
    { label: 'Edges', ports: ['edge.bottom', 'edge.right', 'edge.top', 'edge.left'] },
    { label: 'Angles', ports: ['angle'] },
    { label: 'Scalars', ports: ['a', 'b', 'h', 's1', 's2', 'd1', 'd2', 'area', 'perimeter'] },
  ],
  Point: () => [
    { label: 'Points', ports: ['point'] },
    { label: 'Scalars', ports: ['x', 'y'] },
  ],
  Line: () => [
    { label: 'Points', ports: ['from', 'to'] },
    { label: 'Edges', ports: ['edge.line'] },
    { label: 'Scalars', ports: ['length'] },
  ],
  Polygon: (params) => {
    const vertices = Array.isArray(params?.['vertices']) ? (params!['vertices'] as unknown[]) : [];
    const n = vertices.length || 3;
    return [
      { label: 'Points', ports: Array.from({ length: n }, (_, i) => `point[${i}]`) },
      { label: 'Edges', ports: Array.from({ length: n }, (_, i) => `edge[${i}]`) },
      { label: 'Scalars', ports: ['area', 'perimeter'] },
    ];
  },
  Diagonal: () => [
    { label: 'Points', ports: ['intersection'] },
    { label: 'Scalars', ports: ['d1', 'd2'] },
  ],
  Altitude: () => [
    { label: 'Points', ports: ['foot'] },
    { label: 'Scalars', ports: ['length'] },
  ],
  Median: () => [
    { label: 'Points', ports: ['midpoint'] },
    { label: 'Scalars', ports: ['length'] },
  ],
  Midsegment: () => [
    { label: 'Points', ports: ['midpoint1', 'midpoint2'] },
    { label: 'Scalars', ports: ['length'] },
  ],
};

// Derive vertex/edge name maps from PORT_GROUPS_BY_TYPE for label/style logic
function deriveNamesFromPortGroup(label: string, prefix: string): Record<string, readonly string[]> {
  const result: Record<string, readonly string[]> = {};
  for (const [type, fn] of Object.entries(PORT_GROUPS_BY_TYPE)) {
    const groups = fn();
    const group = groups.find((g) => g.label === label);
    if (group) {
      const names = group.ports.filter((p) => p.startsWith(`${prefix}.`)).map((p) => p.slice(prefix.length + 1));
      if (names.length > 0) {
        result[type] = names;
      }
    }
  }
  return result;
}

const DERIVED_VERTEX_NAMES = deriveNamesFromPortGroup('Points', 'point');
const DERIVED_EDGE_NAMES = deriveNamesFromPortGroup('Edges', 'edge');

export function getVertexNames(elementType: string): readonly string[] {
  return DERIVED_VERTEX_NAMES[elementType] ?? [];
}

export function getEdgeNames(elementType: string): readonly string[] {
  return DERIVED_EDGE_NAMES[elementType] ?? [];
}

// =============================================================================
// SUBPART LABEL DEFAULTS (compute effective label state from global config)
// =============================================================================

/** Edge-to-property mappings per shape type. */
const EDGE_PROPERTY_MAPS: Record<string, Record<string, string>> = {
  Triangle: TRIANGLE_PROPERTY_FOR_EDGE,
  Rectangle: RECTANGLE_PROPERTY_FOR_EDGE,
  Square: SQUARE_PROPERTY_FOR_EDGE,
  Parallelogram: PARALLELOGRAM_PROPERTY_FOR_EDGE,
  Rhombus: RHOMBUS_PROPERTY_FOR_EDGE,
  Trapezoid: TRAPEZOID_PROPERTY_FOR_EDGE,
};

/** Edge ordering per shape (determines dedup priority). Derived from PORT_GROUPS_BY_TYPE. */
const EDGE_LABEL_ORDER = DERIVED_EDGE_NAMES;

/** Shapes that deduplicate edge labels (only first edge per property gets label). */
const EDGE_DEDUP_SHAPES = new Set(['Rectangle', 'Square', 'Parallelogram', 'Rhombus']);

interface AngleLabelInfo {
  names: readonly string[];
  givenMap: Record<string, string>;
  /** Maps angle name to exterior angle param key (e.g. alpha → extAlpha). */
  extAngleMap?: Record<string, string>;
  labelledIndices?: readonly number[];
}

const ANGLE_INFO: Record<string, AngleLabelInfo> = {
  Triangle: {
    names: TRIANGLE_ANGLE_NAMES,
    givenMap: { alpha: 'alpha', beta: 'beta', gamma: 'gamma' },
    extAngleMap: { alpha: 'extAlpha', beta: 'extBeta', gamma: 'extGamma' },
  },
  Parallelogram: {
    names: ['alpha', 'beta', 'gamma', 'delta'],
    givenMap: { alpha: 'angle' },
    labelledIndices: [0, 1],
  },
  Rhombus: {
    names: ['alpha', 'beta', 'gamma', 'delta'],
    givenMap: { alpha: 'angle' },
    labelledIndices: [0, 1],
  },
  Trapezoid: {
    names: ['alpha', 'beta', 'gamma', 'delta'],
    givenMap: { alpha: 'angle' },
  },
};

/**
 * Compute default label visibility for each subpart (edge + angle) based on
 * the global geometry display config and element params.
 *
 * Returns a map from subpart name to boolean (true = label shown by default).
 * Returns null for element types without label logic.
 */
export function computeSubpartLabelDefaults(
  elementType: string,
  params: Record<string, unknown> | undefined,
  geometryConfig: GeometryDisplayConfig | undefined,
): Record<string, boolean> | null {
  const namesScope = geometryConfig?.label?.names;
  const valuesScope = geometryConfig?.label?.values;

  const givenKeys = new Set(Object.keys(params ?? {}));

  const edgeMap = EDGE_PROPERTY_MAPS[elementType];
  const edgeOrder = EDGE_LABEL_ORDER[elementType];
  if (!edgeMap || !edgeOrder) {
    return null;
  }

  const result: Record<string, boolean> = {};
  const dedup = EDGE_DEDUP_SHAPES.has(elementType);
  const shownProperties = new Set<string>();

  for (const edgeName of edgeOrder) {
    const prop = edgeMap[edgeName];
    const isGiven = givenKeys.has(prop);
    const showName = namesScope === 'all' || namesScope === 'tiered' || (namesScope === 'given' && isGiven);
    const showValue = valuesScope === 'all' || valuesScope === 'tiered' || (valuesScope === 'given' && isGiven);
    const dedupOk = !dedup || !shownProperties.has(prop);
    if (dedup) {
      shownProperties.add(prop);
    }
    result[edgeName] = dedupOk && (showName || showValue);
  }

  const angleInfo = ANGLE_INFO[elementType];
  if (angleInfo) {
    for (let i = 0; i < angleInfo.names.length; i++) {
      const angleName = angleInfo.names[i];
      const givenProp = angleInfo.givenMap[angleName];
      const extProp = angleInfo.extAngleMap?.[angleName];
      const isGiven = givenProp ? givenKeys.has(givenProp) : false;
      const isExtGiven = extProp ? givenKeys.has(extProp) : false;
      const showName =
        namesScope === 'all' || namesScope === 'tiered' || (namesScope === 'given' && (isGiven || isExtGiven));
      const showValue =
        valuesScope === 'all' || valuesScope === 'tiered' || (valuesScope === 'given' && (isGiven || isExtGiven));
      const dedupOk = !angleInfo.labelledIndices || angleInfo.labelledIndices.includes(i);
      result[angleName] = dedupOk && (showName || showValue);
    }
  }

  return result;
}

// =============================================================================
// SUBPART VISIBLE DEFAULTS (compute effective visibility from global config)
// =============================================================================

/**
 * Compute default visibility for each subpart based on shape defaults.
 *
 * - **Edges**: always visible (`true`)
 * - **Angles**: always visible (`true`) — shape defaults own angle visibility,
 *   not scope. Rectangles/squares hide angles via `createRightAngleMarkers`
 *   which defaults to `visible: false`, but that's handled at creation time.
 *
 * Returns a map from subpart name to boolean (true = rendered by default).
 * Returns null for element types without subpart visibility logic.
 */
export function computeSubpartVisibleDefaults(
  elementType: string,
  _params: Record<string, unknown> | undefined,
  _geometryConfig: GeometryDisplayConfig | undefined,
): Record<string, boolean> | null {
  const edgeOrder = EDGE_LABEL_ORDER[elementType];
  if (!edgeOrder) {
    return null;
  }

  const result: Record<string, boolean> = {};

  // Edges are always visible
  for (const edgeName of edgeOrder) {
    result[edgeName] = true;
  }

  // Angles are visible by default (shape defaults own visibility)
  const angleInfo = ANGLE_INFO[elementType];
  if (angleInfo) {
    for (const angleName of angleInfo.names) {
      result[angleName] = true;
    }
  }

  return result;
}

// =============================================================================
// ELEMENT PORT GROUPS (auto-expands decomposed sub-ports from PORT_GROUPS_BY_TYPE)
// =============================================================================

/**
 * Returns port groups (Points, Edges, Angles, Scalars) for a geometry element type.
 * Automatically appends decomposed sub-ports (.x/.y for points, .length/.start/.end for edges)
 * to the Scalars group.
 */
export function getElementPortGroups(type: string, params?: Record<string, unknown>): GeometryPortGroup[] {
  const fn = PORT_GROUPS_BY_TYPE[type];
  if (!fn) {
    return [];
  }

  const rawGroups = fn(params);

  // Collect decomposed sub-ports from Points and Edges groups
  const decomposed: string[] = [];
  for (const group of rawGroups) {
    if (group.label === 'Points') {
      for (const port of group.ports) {
        decomposed.push(`${port}.x`, `${port}.y`);
      }
    } else if (group.label === 'Edges') {
      for (const port of group.ports) {
        decomposed.push(`${port}.length`, `${port}.start.x`, `${port}.start.y`, `${port}.end.x`, `${port}.end.y`);
      }
    }
  }

  if (decomposed.length === 0) {
    return rawGroups;
  }

  // Append decomposed sub-ports to the Scalars group
  const hasScalars = rawGroups.some((g) => g.label === 'Scalars');
  const result = rawGroups.map((g) =>
    g.label === 'Scalars' ? { label: 'Scalars', ports: [...g.ports, ...decomposed] } : g,
  );
  if (!hasScalars) {
    result.push({ label: 'Scalars', ports: decomposed });
  }
  return result;
}

/**
 * Returns all fully-qualified port paths for a set of elements.
 * e.g., ['t1.point.A', 't1.a', 'r1.w', ...]
 */
export function getAllAvailablePortPaths(elements: Record<string, GeometryElementDef>): string[] {
  const paths: string[] = [];
  for (const [id, def] of Object.entries(elements)) {
    const params = (def as unknown as Record<string, unknown>)['params'] as Record<string, unknown> | undefined;
    const groups = getElementPortGroups(def.type, params);
    for (const group of groups) {
      for (const port of group.ports) {
        paths.push(`${id}.${port}`);
      }
    }
  }
  return paths;
}
